import clsx from 'clsx';
import { ChevronDown } from 'mdi-material-ui';
import React, { useMemo, useRef, useEffect, useState } from 'react';

import { Typography, Button } from '../../Elements';

export type CollapsibleSectionContainerV2Props = {
	actionButton?: React.ReactElement | undefined | false;
	children: React.ReactElement;
	title: string;
	titleIcon?: React.ReactElement;
	titleIconAlign?: 'left' | 'right';
	expandedOnLoad?: boolean;
	classNames?: string;
	dataTestId: string;
	titleClassNames?: string;
	customButtons?: {
		expandedButton: React.ReactElement;
		expandedButtonClass: string;
		closedButton: React.ReactElement;
		closedButtonClass: string;
	};
	chevronAlign?: 'left' | 'right';
	canBeExpanded?: boolean; // if false, hide the chevron and disable collapsing
	doExpand?: boolean; // when changed the panel will expand if true, this is used for when the expansion state needs to be controlled externally
};

export const CollapsibleSectionContainerV2 = (props: CollapsibleSectionContainerV2Props) => {
	const {
		actionButton,
		children,
		title,
		titleIcon,
		titleIconAlign = 'left',
		expandedOnLoad = true,
		classNames = '',
		dataTestId = '',
		titleClassNames = '',
		customButtons,
		chevronAlign = 'left',
		canBeExpanded = true,
		doExpand,
	} = props;

	const [isExpanded, setIsExpanded] = useState(expandedOnLoad);
	const [containerHeight, setContainerHeight] = useState<string>(expandedOnLoad ? 'auto' : '0px');

	const contentDivRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!canBeExpanded) {
			setIsExpanded(false);
			setContainerHeight('0px');
		}
	}, [canBeExpanded]);

	useEffect(() => {
		// listener for changes to the doExpand prop. If it changes to true and the panel is not already expanded, expand the panel
		if (doExpand && !isExpanded) {
			toggleExpanded();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [doExpand]);

	const toggleExpanded = () => {
		const newIsExpanded = !isExpanded;
		setIsExpanded(newIsExpanded);
		const contentEl = contentDivRef.current;
		const containerEl = containerRef.current;
		if (!contentEl || !containerEl) return;

		const expandedTargetPx = `${contentEl.scrollHeight}px`;
		setContainerHeight(expandedTargetPx);

		if (!newIsExpanded) {
			// Force reflow
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setContainerHeight('0px');
				});
			});
		}
	};

	// A11y ids
	const contentId = useMemo(() => `${dataTestId}-collapsible-content`, [dataTestId]);
	const titleId = useMemo(() => `${dataTestId}-collapsible-title`, [dataTestId]);

	const handleHeightTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
		if (e.target !== e.currentTarget) return;
		if (e.propertyName !== 'height') return;

		if (isExpanded) setContainerHeight('auto');
	};

	const chevronButton = canBeExpanded && (
		<Button
			variant={customButtons ? (isExpanded ? 'secondary' : 'primary') : 'icon'}
			size={customButtons ? 'lg' : 'icon'}
			onClick={toggleExpanded}
			className={`${
				customButtons
					? isExpanded
						? customButtons.expandedButtonClass
						: customButtons.closedButtonClass
					: 'w-[32px] h-[32px] !bg-primaryBrandBackground hover:!bg-primaryBrandBackground rounded-full'
			}`}
			aria-expanded={isExpanded}
			aria-controls={contentId}
		>
			{customButtons ? (
				isExpanded ? (
					<>{customButtons.expandedButton}</>
				) : (
					<>{customButtons.closedButton}</>
				)
			) : (
				<ChevronDown
					className={`text-primaryBrand !text-[24px] !transition-transform !ease-in-out !duration-500 ${
						isExpanded ? 'transform rotate-180' : 'rotate-0'
					}`}
				/>
			)}
			<span className='sr-only'>{isExpanded ? 'Collapse' : 'Expand'}</span>
		</Button>
	);

	return (
		<div
			className={clsx('w-full bg-infoPanelBackground p-4 rounded-md', classNames)}
			data-test-id={`${dataTestId}-collapsible-container`}
		>
			<div className='flex gap-4 items-center'>
				<div className='flex flex-row justify-between items-center w-full pb-2'>
					<div
						className={`flex flex-row justify-between items-center ${chevronAlign === 'left' ? 'w-full' : ''}`}
					>
						<div className='flex flex-row items-center'>
							{chevronAlign === 'left' && chevronButton}
							<button
								type='button'
								onClick={canBeExpanded ? toggleExpanded : undefined}
								className={`flex items-center justify-center ${chevronAlign === 'left' ? 'pl-3' : 'pr-3'} ${canBeExpanded ? 'cursor-pointer' : 'cursor-auto'}`}
								data-test-id='collapse-expand-toggle-button'
								aria-expanded={isExpanded}
								aria-controls={contentId}
							>
								{titleIcon && titleIconAlign === 'left' && titleIcon}
								<Typography
									variant='heading_4_medium'
									id={titleId}
									className={`text-primaryTextDark mr-2 text-left ${
										titleIcon && titleIconAlign === 'left' ? 'ml-2' : ''
									} ${titleClassNames}`}
								>
									{title}
								</Typography>
								{titleIcon && titleIconAlign === 'right' && titleIcon}
							</button>
						</div>
						{actionButton && canBeExpanded && (
							<div className='flex flex-row items-center'>{actionButton}</div>
						)}
					</div>
					{chevronAlign === 'right' && chevronButton}
				</div>
			</div>

			<div
				id={contentId}
				ref={containerRef}
				role='region'
				aria-labelledby={titleId}
				aria-hidden={!isExpanded}
				className='overflow-hidden transition-height duration-500 ease-linear'
				style={{ height: containerHeight }}
				onTransitionEnd={handleHeightTransitionEnd}
			>
				<div ref={contentDivRef}>{children}</div>
			</div>
		</div>
	);
};
